import { ChevronDownIcon } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import { useTranslation } from 'react-i18next';

import NotificationStoreContext from '@/context/NotificationStoreContext';
import User from '@/domain/core/User';
import { TNotificationStore } from '@/domain/notification/NotificationStore';
import {
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuLabel,
	DropdownMenuTrigger,
} from '@/ui/DropdownMenu';
import { Text } from '@/ui/Text';
import { Tooltip, TooltipArrow, TooltipContent, TooltipProvider, TooltipTrigger } from '@/ui/Tooltip';
import { cn } from '@/utils';

interface UserProfileProps {
	hasInfo: boolean;
}

const UserProfile = observer((props: UserProfileProps) => {
	const { hasInfo } = props;
	const { t } = useTranslation();
	const currentRole = User.currentRole?.name;
	const roleDescription =
		User.currentRole?.label ||
		(currentRole ? t(`roles.list.${currentRole}`) : '') ||
		User.role.label ||
		User.role.name ||
		'';

	const [selectedRole, selectRole] = React.useState<number>(() => User.role.id);
	const [isRoleMenuOpen, setIsRoleMenuOpen] = React.useState(false);

	const { getNotificationCountByRole } = React.useContext<TNotificationStore>(NotificationStoreContext);

    const userPhoto = typeof User?.profile?.photo === 'object' ? User?.profile?.photo?.url : User?.profile?.photo;

	return (
		<div
			className="flex gap-[9px] items-center flex-nowrap max-w-[260px] flex-[0]"
			role="region"
			aria-label={t('accessibility.userProfile.region')}
		>
			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger asChild>
						<div>
							<Link href={`/account/${User.id}/profile`} passHref>
								<a
									aria-label={t('accessibility.userProfile.profileLink', {
										name: User.profile.email,
									})}
								>
									<div
										className="bg-gradient-to-b from-white to-brand-gray-8 text-brand-dark-blue border border-brand-gray-4 p-[6px] rounded-lg cursor-pointer"
										role="img"
										aria-label={t('accessibility.userProfile.avatar', {
											name: User.profile.email,
										})}
									>
										<div className="size-10 flex items-center justify-center relative">
											<Image
												layout="fill"
												objectFit="cover"
												objectPosition="center center"
												src={userPhoto || '/img/png/no-photo.png'}
												width={24}
												height={24}
												className="size-6 rounded-lg"
												alt={t('accessibility.userProfile.avatarAlt', {
													name: User.profile.email,
												})}
											/>
										</div>
									</div>
								</a>
							</Link>
						</div>
					</TooltipTrigger>
					<TooltipContent>
						<TooltipArrow />
						{t('profile.goTo')}
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>

			{hasInfo && (
				<div className="flex flex-col justify-between gap-1 py-[9px]" style={{ wordBreak: 'break-word' }}>
					<TooltipProvider>
						<Tooltip>
							<TooltipTrigger asChild>
								<Text
									variant="description"
									overflow="ellipsis"
									className={cn('text-brand-dark line-clamp-2 break-words w-full')}
									aria-label={t('accessibility.userProfile.currentRole', { role: roleDescription })}
								>
									{roleDescription}
								</Text>
							</TooltipTrigger>
							<TooltipContent>
								<TooltipArrow />
								{roleDescription}
							</TooltipContent>
						</Tooltip>
					</TooltipProvider>
					{User.availableRoles.length > 1 && (
						<DropdownMenu open={isRoleMenuOpen} onOpenChange={setIsRoleMenuOpen}>
							<DropdownMenuTrigger asChild>
								<button
									aria-label={t('accessibility.userProfile.changeRoleButton')}
									aria-expanded={isRoleMenuOpen}
									aria-haspopup="menu"
									aria-controls="role-menu"
									type="button"
									className="flex gap-1 text-brand-blue whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-blue-500"
								>
									<Text variant="labelDescription">{t('profile.changeRole.l')}</Text>
									<ChevronDownIcon
										className="flex size-[calc(12px*var(--font-scale,1))] overflow-visible"
										strokeWidth={3}
										aria-hidden="true"
									/>
								</button>
							</DropdownMenuTrigger>
							<DropdownMenuContent
								id="role-menu"
								role="menu"
								aria-label={t('accessibility.userProfile.roleMenu')}
							>
								<DropdownMenuLabel id="role-menu-label">
									{t('profile.changeRole.system')}
								</DropdownMenuLabel>
								{User.availableRoles.map((role) => {
									const notificationCount = getNotificationCountByRole(role.id);
									const isSelected = selectedRole === role.id;
									return (
										<DropdownMenuCheckboxItem
											key={role.id}
											role="menuitemradio"
											aria-checked={isSelected}
											aria-labelledby={`role-item-${role.id}`}
											checked={isSelected}
											onCheckedChange={(checked) => {
												if (checked) {
													User.setCurrentRole(role.id);
													selectRole(role.id);
												}
											}}
											className="max-w-[240px] [&>span]:relative"
										>
											<span id={`role-item-${role.id}`}>{role.label || role.name}</span>
											{notificationCount > 0 && (
												<span
													className="absolute -right-3 top-0 z-10 min-w-[18px] h-[18px] flex justify-center items-center rounded-full text-white bg-[#F93333] p-1 text-xs"
													aria-label={t('accessibility.userProfile.roleNotificationCount', {
														count: notificationCount,
													})}
													aria-hidden="true"
												>
													{notificationCount < 100 ? notificationCount : '99+'}
												</span>
											)}
										</DropdownMenuCheckboxItem>
									);
								})}
							</DropdownMenuContent>
						</DropdownMenu>
					)}
				</div>
			)}
		</div>
	);
});

export { UserProfile };
